import { useCallback, useEffect } from 'react';
import { useCollection } from '@/features/events/contexts/CollectionContext/useCollection';
import { useCurrentDestination } from '@/features/interactions/addToCalendar/hooks/useCurrentDestination';
import { useExportFormStatus } from '@/features/interactions/addToCalendar/hooks/useExportFormStatus';
import { useExportModalStore } from '@/features/interactions/addToCalendar/hooks/useExportModalStore';
import type { Destination } from '@/features/interactions/addToCalendar/types';
import {
  Activity,
  useMultiExportStore,
} from '@/features/magic/hooks/useMultiExportStore';
import {
  type LaunchParams,
  useLauncherAction,
  type LaunchOption,
} from '@/hooks/useLauncher';
import { useQueryParams } from '@/hooks/useQueryParams';
import { PlatformTypes } from '@/types/gql.generated';
import { isPlatformType } from '@/utils/platformLib';
import { LAUNCH_KEY_GOOGLE_MULTI } from '../util.lib';

type Arg = {
  launch: LaunchOption;
};

const platformsWithModal: string[] = [PlatformTypes.GoogleIntegration];

export const useExportModalLaunch = ({ launch }: Arg) => {
  const { queryParams, removeQueryParam } = useQueryParams();
  const { events: entries } = useCollection();
  const { destination } = useCurrentDestination();
  const { open: openControl } = useMultiExportStore();

  const {
    isOpen,
    openPlatform,
    isModalWorkflow,
    setOpenPlatform,
    setIsModalWorkflow,
    closeModal,
    eventId: openEventId,
  } = useExportModalStore();

  // Destination-scoped, merged local + server view — the same rule the export
  // form shows — so "everything here is already on that calendar" survives
  // navigation and reloads instead of resetting with the in-memory store.
  const { isExported: hasExportedDestination } = useExportFormStatus({
    destination,
    magicEvent: null,
    events: entries,
  });
  const isMultiAdd = launch === LAUNCH_KEY_GOOGLE_MULTI;
  const eventId = isMultiAdd ? null : launch?.split(':')[1] || null;

  const isRejected = queryParams.get('code') === 'PERMISSION_NOT_GRANTED';

  const handleLauncherAction = useCallback(
    (params: LaunchParams) => {
      if (isRejected) {
        removeQueryParam('code');
      } else if (isPlatformType(params.platform)) {
        setOpenPlatform(params.platform, eventId);
        if (platformsWithModal.includes(params.platform)) {
          setIsModalWorkflow(true);
        }
      }
    },
    [setOpenPlatform, setIsModalWorkflow, eventId, isRejected, removeQueryParam]
  );

  useLauncherAction(launch, handleLauncherAction);

  useEffect(() => {
    // When users set up a Google Calendar integration when clicking the button,
    // we drop them into a modal where they select the calendar and the drawer
    // is closed. Automatically open the drawer to show that the export has
    // succeeded
    if (isMultiAdd && hasExportedDestination && isModalWorkflow) {
      openControl(Activity.GoogleCalendar);
      setIsModalWorkflow(false);
    }
  }, [
    isMultiAdd,
    hasExportedDestination,
    openControl,
    isModalWorkflow,
    setIsModalWorkflow,
  ]);

  const handleClose = useCallback(
    (destination: Destination | null = null) => {
      closeModal(destination);
    },
    [closeModal]
  );

  return {
    isOpen,
    openPlatform,
    openEventId,
    onClose: handleClose,
    hasExportedDestination,
  };
};
